import { DataAccess } from "../../../dataaccess/data.access";
import type { StudentEngagementUserLoginStatusQueryValue } from "../../../pojo/StudentEngagementUserLoginStatus";
import { PaginateParams } from "../../../pojo/S25WsNode";
import { Proto } from "../../../pojo/Proto";
import {
    buildCustomAttributeFiltersParam,
    type SeUserSearchCustomAttributeFilter,
} from "./se.user.search.custom.attribute.util";

/** Matches {@code com.collegenet.series25.document.studentengagement.UserTileOrganization}. */
export type SeUserTileOrganization = {
    organizationId: number;
    organizationName: string;
    roleNames?: string[];
};

/** Matches {@code com.collegenet.series25.document.studentengagement.UserTileAttendedEvent}. */
export type SeUserTileAttendedEvent = {
    eventId: number;
    eventName?: string;
    startDate?: string;
};

export type SeUserTileDocument = {
    itemId: number;
    itemName: string;
    itemTypeId: number;
    txt: string;
    email?: string;
    userStatus?: string;
    organizations?: SeUserTileOrganization[];
    eventsAttended?: SeUserTileAttendedEvent[];
    textComment?: string;
};

export type SeUserSearchResponse = Proto.Paginate & {
    users?: SeUserTileDocument[];
    err_code?: string;
    err_msg?: string;
};

/** Optional filters for {@link SeUserSearchService.searchUsers} (tile query params). */
export type SeUserSearchFilters = {
    organizationIds?: number[];
    roleIds?: number[];
    customAttributeFilters?: SeUserSearchCustomAttributeFilter[];
    userStatus?: StudentEngagementUserLoginStatusQueryValue;
};

export class SeUserSearchService {
    public static async searchUsers(
        searchText: string,
        paginateParams: PaginateParams = { page: 1, page_size: 25, paginate: "T" },
        paginateKey?: string | null,
        filters?: SeUserSearchFilters | null,
    ): Promise<SeUserSearchResponse> {
        const trimmed = searchText?.trim() ?? "";
        const organizationIds =
            filters?.organizationIds?.filter((organizationId) => !Number.isNaN(organizationId) && organizationId > 0) ??
            [];
        const roleIds = filters?.roleIds?.filter((roleId) => !Number.isNaN(roleId) && roleId > 0) ?? [];
        const userStatusTrimmed = filters?.userStatus?.trim();
        const customAttributeFiltersParam = buildCustomAttributeFiltersParam(filters?.customAttributeFilters);
        const queryString = DataAccess.params({
            page_num: paginateParams.page,
            page_size: paginateParams.page_size,
            ...(trimmed ? { q: trimmed } : {}),
            ...(paginateKey ? { paginate_key: paginateKey } : {}),
            ...(organizationIds.length ? { org_ids: organizationIds.join(",") } : {}),
            ...(roleIds.length ? { role_ids: roleIds.join(",") } : {}),
            ...(customAttributeFiltersParam ? { cust_atrb_filters: customAttributeFiltersParam } : {}),
            ...(userStatusTrimmed ? { user_status: userStatusTrimmed } : {}),
        });
        return DataAccess.get<SeUserSearchResponse>(
            DataAccess.injectCaller(`/tile/users.json?${queryString}`, "SeUserSearchService.searchUsers"),
        );
    }
}
